import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ShieldCheck,
  Luggage,
  Store,
  MapPin,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Maximize2,
} from 'lucide-react';
import {
  CopyAction,
  HelpTooltip,
  ImageFullScreenView,
  Tag,
  type TagProps,
} from '@chekinapp/ui';
import {type RadicalStorageBooking} from '@chekin/core';
import {getBookingLifecycle, type BookingLifecycle} from 'utils/upselling';
import styles from './styles.module.css';

type Props = {
  booking: RadicalStorageBooking;
  defaultExpanded?: boolean;
};

function formatInStorageTz(
  value: string,
  tz: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string {
  const date = new Date(value);
  try {
    return new Intl.DateTimeFormat(locale || 'en-GB', {
      ...options,
      timeZone: tz || 'UTC',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-GB', {...options, timeZone: 'UTC'}).format(date);
  }
}

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
};
const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

const BADGE: Record<
  BookingLifecycle,
  {icon: typeof ShieldCheck; labelKey: string; tone: TagProps['tone']}
> = {
  active: {
    icon: ShieldCheck,
    labelKey: 'luggage_booking.status_confirmed',
    tone: 'info',
  },
  completed: {
    icon: CheckCircle2,
    labelKey: 'luggage_booking.status_completed',
    tone: 'neutral',
  },
};

function BookingCard({booking, defaultExpanded = false}: Props) {
  const {t, i18n} = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isQrFullScreen, setIsQrFullScreen] = useState(false);

  if (!booking?.pnr) {
    return null;
  }

  const phase: BookingLifecycle = getBookingLifecycle(booking);
  const isActive = phase === 'active';
  const badge = BADGE[phase];
  const BadgeIcon = badge.icon;

  const inTz = (value: string, options: Intl.DateTimeFormatOptions) =>
    formatInStorageTz(value, booking.storage_timezone, i18n.language, options);

  const recap =
    !isActive && booking.drop_off && booking.pick_up
      ? [
          inTz(booking.drop_off, DATE_OPTS),
          `${inTz(booking.drop_off, TIME_OPTS)} → ${inTz(booking.pick_up, TIME_OPTS)}`,
          t('luggage_form.bags_count', {count: booking.bags}),
        ].join(' · ')
      : null;

  return (
    <div className={isActive ? styles.card : `${styles.card} ${styles.cardPast}`}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setExpanded(value => !value)}
        aria-expanded={expanded}
      >
        <span className={styles.headerMain}>
          <span className={styles.title}>
            {recap && booking.storage_name
              ? booking.storage_name
              : t('luggage_booking.title')}
          </span>
          {recap ? (
            <span className={styles.headerStorage}>{recap}</span>
          ) : (
            booking.storage_name && (
              <span className={styles.headerStorage}>{booking.storage_name}</span>
            )
          )}
        </span>
        <Tag size="sm" tone={badge.tone} className={styles.badgeSlot}>
          <BadgeIcon size={13} />
          {t(badge.labelKey)}
        </Tag>
        <ChevronDown
          size={18}
          className={expanded ? styles.chevronOpen : styles.chevron}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <>
          {isActive && booking.qrcode && (
            <div className={styles.stub}>
              <button
                type="button"
                className={styles.qrFrame}
                onClick={() => setIsQrFullScreen(true)}
                aria-label={t('luggage_booking.qr_alt')}
              >
                <img
                  className={styles.qr}
                  src={booking.qrcode}
                  alt={t('luggage_booking.qr_alt')}
                  width={200}
                  height={200}
                />
                <span className={styles.qrZoomHint}>
                  <Maximize2 size={13} aria-hidden="true" />
                  <span className={styles.qrZoomHintTouch}>
                    {t('luggage_booking.qr_zoom_hint')}
                  </span>
                  <span className={styles.qrZoomHintPointer}>
                    {t('luggage_booking.qr_zoom_hint_pointer')}
                  </span>
                </span>
              </button>
              <div className={styles.stubCaption}>{t('luggage_booking.qr_caption')}</div>
              <div className={styles.stubHint}>{t('luggage_booking.qr_hint')}</div>
            </div>
          )}

          {isActive && <div className={styles.tear} aria-hidden="true" />}

          <div className={styles.body}>
            {booking.drop_off && booking.pick_up && (
              <div className={styles.scheduleBlock}>
                <div className={styles.schedule}>
                  <div className={styles.scheduleCol}>
                    <span className={styles.scheduleLabel}>
                      {t('luggage_form.drop_off')}
                    </span>
                    <span className={styles.scheduleTime}>
                      {formatInStorageTz(
                        booking.drop_off,
                        booking.storage_timezone,
                        i18n.language,
                        TIME_OPTS,
                      )}
                    </span>
                    <span className={styles.scheduleDate}>
                      {formatInStorageTz(
                        booking.drop_off,
                        booking.storage_timezone,
                        i18n.language,
                        DATE_OPTS,
                      )}
                    </span>
                  </div>
                  <span className={styles.scheduleArrow} aria-hidden="true">
                    <ArrowRight size={22} />
                  </span>
                  <div className={`${styles.scheduleCol} ${styles.scheduleColEnd}`}>
                    <span className={styles.scheduleLabel}>
                      {t('luggage_form.pick_up')}
                    </span>
                    <span className={styles.scheduleTime}>
                      {formatInStorageTz(
                        booking.pick_up,
                        booking.storage_timezone,
                        i18n.language,
                        TIME_OPTS,
                      )}
                    </span>
                    <span className={styles.scheduleDate}>
                      {formatInStorageTz(
                        booking.pick_up,
                        booking.storage_timezone,
                        i18n.language,
                        DATE_OPTS,
                      )}
                    </span>
                  </div>
                </div>
                <span className={styles.scheduleTz}>
                  {t('luggage_booking.time_hint')}
                </span>
              </div>
            )}

            {isActive && (
              <div className={styles.codes}>
                <div className={styles.codeRow}>
                  <span className={styles.fieldLabel}>
                    {t('luggage_booking.code_label')}
                    <HelpTooltip content={t('luggage_booking.code_hint')} size={16} />
                  </span>
                  <div className={styles.codeValueRow}>
                    <span className={styles.codeValue}>{booking.pnr}</span>
                    <CopyAction
                      size="md"
                      value={booking.pnr}
                      label={t('luggage_booking.copy')}
                      copiedLabel={t('copied')}
                    />
                  </div>
                </div>

                {booking.recovery_code && (
                  <div className={styles.codeRow}>
                    <span className={styles.fieldLabel}>
                      {t('luggage_booking.recovery_code')}
                      <HelpTooltip
                        content={t('luggage_booking.recovery_hint')}
                        size={16}
                      />
                    </span>
                    <span className={styles.codeValue}>{booking.recovery_code}</span>
                  </div>
                )}
              </div>
            )}

            <ul className={styles.meta}>
              <li className={styles.metaItem}>
                <span className={styles.metaIcon}>
                  <Luggage size={16} />
                </span>
                <span className={styles.metaText}>
                  <span className={styles.metaLabel}>{t('luggage_booking.bags')}</span>
                  <span className={styles.metaValue}>{booking.bags}</span>
                </span>
              </li>
              {booking.storage_name && (
                <li className={styles.metaItem}>
                  <span className={styles.metaIcon}>
                    <Store size={16} />
                  </span>
                  <span className={styles.metaText}>
                    <span className={styles.metaLabel}>
                      {t('luggage_booking.storage')}
                    </span>
                    <span className={styles.metaValue}>{booking.storage_name}</span>
                  </span>
                </li>
              )}
              {booking.storage_address && (
                <li className={styles.metaItem}>
                  <span className={styles.metaIcon}>
                    <MapPin size={16} />
                  </span>
                  <span className={styles.metaText}>
                    <span className={styles.metaLabel}>
                      {t('luggage_booking.address')}
                    </span>
                    <span className={styles.metaValue}>{booking.storage_address}</span>
                  </span>
                </li>
              )}
            </ul>
          </div>
        </>
      )}

      {isQrFullScreen && booking.qrcode && (
        <ImageFullScreenView
          src={booking.qrcode}
          alt={t('luggage_booking.qr_alt')}
          onClose={() => setIsQrFullScreen(false)}
        />
      )}
    </div>
  );
}

export {BookingCard};
